// Phase H.4 — Recurring series performance widget.
//
// Display-only component used in two places:
//   1. /admin/recurring-series → template detail (one template's perf)
//   2. /bi/dashboard "Recurring" tab (overall + per-template breakdown)
//
// Metrics displayed:
//   - runs, on-time rate (first-applies definition), avg margin per leg
//   - delay total/avg, stage distribution donut, runs-per-week bar chart
import React from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, PieChart, Pie, Cell, Legend,
} from "recharts";
import { Activity, Loader2, AlertCircle, Repeat2, Target, TrendingUp, Clock, Layers } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});
const syne = (size, weight, ls) => ({
  fontFamily: "Syne, sans-serif",
  fontSize: size,
  fontWeight: weight || 700,
  letterSpacing: ls,
});

const fmtNum = (v, d = 0) => (v == null ? "—" : Number(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));
const fmtMoney = (v) => (v == null ? "—" : `€ ${fmtNum(v, 2)}`);
const fmtPct = (v) => (v == null ? "—" : `${(Number(v) * 100).toFixed(1)} %`);
const fmtMin = (v) => (v == null ? "—" : `${fmtNum(v, 0)} min`);

const STAGE_COLORS = ["#9ec3ec", "#0a8754", "#f3b14a", "#b45309", "#94a3b8", "#ef4444"];

const useTemplatePerformance = (tid) =>
  useQuery({
    queryKey: ["bi-recurring-perf", tid],
    queryFn: () => axios.get(`${API}/bi/recurring/${tid}/performance`).then((r) => r.data),
    enabled: !!tid,
    staleTime: 30_000,
  });

export const useRecurringPerfSummary = () =>
  useQuery({
    queryKey: ["bi-recurring-summary"],
    queryFn: () => axios.get(`${API}/bi/recurring/summary`).then((r) => r.data),
    staleTime: 30_000,
  });

const KpiBlock = ({ label, value, sub, icon: Icon, tone = "#9ec3ec", testid }) => (
  <div
    className="rounded-lg p-3"
    style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }}
    data-testid={testid}
  >
    <div className="flex items-center gap-1.5 mb-1.5">
      <Icon size={11} color={tone} />
      <span style={{ ...mono(8, "#94a3b8", 700, "0.6px"), textTransform: "uppercase" }}>{label}</span>
    </div>
    <div style={{ ...syne(18, 800, "-0.3px"), color: tone }}>{value}</div>
    {sub && <div className="mt-1" style={mono(9, "#94a3b8", 600, "0.3px")}>{sub}</div>}
  </div>
);

const PerfBody = ({ perf, testid }) => {
  if (!perf) return null;
  return (
    <div data-testid={testid}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <KpiBlock label="Runs" tone="#9ec3ec" icon={Repeat2} value={fmtNum(perf.runs)} sub={`${perf.stage_distribution.length} stages`} testid={`${testid}-runs`} />
        <KpiBlock label="On-time rate" tone="#0a8754" icon={Target} value={fmtPct(perf.on_time_rate)} sub={`${perf.on_time_hit ?? 0} / ${perf.on_time_eligible ?? 0} executed`} testid={`${testid}-ontime`} />
        <KpiBlock label="Avg margin / leg" tone="#0a8754" icon={TrendingUp} value={fmtMoney(perf.avg_margin_per_leg)} sub={`Σ ${fmtMoney(perf.margin_total)}`} testid={`${testid}-margin`} />
        <KpiBlock label="Delay (carrier)" tone="#ef4444" icon={Clock} value={fmtMin(perf.delay_total_minutes)} sub={`avg ${fmtMin(perf.delay_avg_minutes)}`} testid={`${testid}-delay`} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Stage distribution donut */}
        {perf.stage_distribution.length > 0 && (
          <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }} data-testid={`${testid}-stage-donut`}>
            <div className="flex items-center gap-1.5 mb-1.5">
              <Layers size={11} color="#9ec3ec" />
              <span style={{ ...mono(9, "#9ec3ec", 700, "0.5px"), textTransform: "uppercase" }}>Stage distribution</span>
            </div>
            <div style={{ width: "100%", height: 200 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={perf.stage_distribution} dataKey="count" nameKey="stage" innerRadius={40} outerRadius={70} paddingAngle={2}>
                    {perf.stage_distribution.map((_, i) => (
                      <Cell key={i} fill={STAGE_COLORS[i % STAGE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 4, color: "#f0f2f8", fontFamily: "'DM Mono', monospace", fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#c5cad8" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Runs-per-week bars */}
        {perf.runs_per_week.length > 0 && (
          <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }} data-testid={`${testid}-runs-week`}>
            <div className="flex items-center gap-1.5 mb-1.5">
              <Activity size={11} color="#9ec3ec" />
              <span style={{ ...mono(9, "#9ec3ec", 700, "0.5px"), textTransform: "uppercase" }}>Runs per week</span>
            </div>
            <div style={{ width: "100%", height: 200 }}>
              <ResponsiveContainer>
                <BarChart data={perf.runs_per_week} margin={{ top: 5, right: 12, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="week" tick={{ fill: "#94a3b8", fontSize: 9 }} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 10 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 4, color: "#f0f2f8", fontFamily: "'DM Mono', monospace", fontSize: 11 }} />
                  <Bar dataKey="count" fill="#9ec3ec" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Compact single-template widget — for the AdminRecurringSeries detail pane.
export default function RecurringSeriesPerfWidget({ templateId }) {
  const { data, isLoading, error } = useTemplatePerformance(templateId);
  if (!templateId) return null;
  if (isLoading) {
    return (
      <div className="rounded-lg p-4 mb-4" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }} data-testid="recurring-perf-loading">
        <Loader2 size={12} className="inline animate-spin mr-2" />Loading performance…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="rounded-lg p-4 mb-4" style={{ background: "rgba(220,53,69,0.06)", border: "1px solid rgba(220,53,69,0.30)" }} data-testid="recurring-perf-error">
        <AlertCircle size={12} className="inline mr-2 text-red-400" />Couldn't load performance.
      </div>
    );
  }
  return (
    <div className="mb-4" data-testid="recurring-perf-widget">
      <div className="flex items-center justify-between mb-2">
        <h3 style={syne(14, 700)}>
          <Activity size={13} className="inline mr-1.5" />
          Series performance
        </h3>
        <span style={mono(9, "#94a3b8", 700, "0.4px")}>on-time fallback: {data.fallback_days}d</span>
      </div>
      <PerfBody perf={data} testid="recurring-perf-body" />
    </div>
  );
}

export { PerfBody, KpiBlock };
